import type { Dispatch, SetStateAction } from "react";
import { Trash2 } from "lucide-react";
import { quantidadeParaNumero } from "@/lib/quantidade";
import { formatPriceCents, parsePriceInput } from "@/components/services/service-meta";
import { agruparMateriais, rotuloDoMaterial } from "@/lib/agrupar-materiais";

export type Item = { materialLineId: string; nome: string; quantidade: string; preco: string };

export const NOVO_MATERIAL = "__novo_material__";

const numero =
  "h-8 w-full rounded border border-zinc-200 bg-white px-2 text-right text-xs tabular-nums dark:border-border dark:bg-card dark:text-foreground";

/** O que chegou na compra: insumo, quantidade e preço, com o "+ Adicionar insumo" embaixo. */
export function ItensDaCompra({
  itens,
  setItens,
  porId,
  grupos,
  onAdicionar,
}: {
  itens: Item[];
  setItens: Dispatch<SetStateAction<Item[]>>;
  porId: Map<string, any>;
  grupos: ReturnType<typeof agruparMateriais>;
  onAdicionar: (materialLineId: string) => void;
}) {
  const mudar = (idx: number, parcial: Partial<Item>) => setItens((v) => v.map((x, j) => (j === idx ? { ...x, ...parcial } : x)));

  return (
    <>
      <table className="w-full table-fixed text-xs">
        <thead className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 dark:border-border dark:text-muted-foreground">
          <tr>
            <th className="pb-1.5 text-left font-semibold">Insumo</th>
            <th className="w-24 pb-1.5 text-right font-semibold">Qtd.</th>
            <th className="w-28 pb-1.5 text-right font-semibold">Preço un.</th>
            <th className="w-24 pb-1.5 text-right font-semibold">Total</th>
            <th className="w-8" />
          </tr>
        </thead>
        <tbody>
          {itens.map((i, idx) => {
            const m: any = porId.get(i.materialLineId);
            const rotulo = m?.name ?? i.nome;
            const linhaCents = Math.round(quantidadeParaNumero(i.quantidade) * (parsePriceInput(i.preco) ?? 0));
            return (
              <tr key={i.materialLineId} className="border-b border-zinc-100 last:border-b-0 dark:border-border/50">
                <td className="py-1.5 pr-2">
                  <span className="block truncate text-xs text-zinc-900 dark:text-foreground">{rotulo}</span>
                  <span className="text-[10px] text-zinc-400 dark:text-muted-foreground/70">
                    {m?.manufacturer?.name}
                    {m?.unit ? ` · ${m.unit}` : ""}
                  </span>
                </td>
                <td className="py-1.5 pr-1.5">
                  <input
                    value={i.quantidade}
                    onChange={(e) => mudar(idx, { quantidade: e.target.value })}
                    inputMode="decimal"
                    aria-label={`Quantidade de ${rotulo}`}
                    className={numero}
                  />
                </td>
                <td className="py-1.5 pr-1.5">
                  <input
                    value={i.preco}
                    onChange={(e) => mudar(idx, { preco: e.target.value })}
                    inputMode="decimal"
                    placeholder="0,00"
                    aria-label={`Preço de ${rotulo}`}
                    className={numero}
                  />
                </td>
                <td className="py-1.5 pr-2 text-right tabular-nums text-zinc-700 dark:text-foreground/80">{formatPriceCents(linhaCents)}</td>
                <td className="py-1.5 text-right">
                  <button
                    type="button"
                    onClick={() => setItens((v) => v.filter((_, j) => j !== idx))}
                    aria-label={`Remover ${rotulo}`}
                    className="rounded p-1 text-zinc-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            );
          })}
          {itens.length === 0 && (
            <tr>
              <td colSpan={5} className="py-4 text-center text-[11.5px] text-zinc-400 dark:text-muted-foreground">
                Escolha abaixo o que você recebeu.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <select
        value=""
        onChange={(e) => onAdicionar(e.target.value)}
        aria-label="Adicionar insumo à entrada"
        className="h-8 w-full rounded-md border border-dashed border-zinc-300 bg-white px-2 text-xs text-zinc-600 dark:border-border dark:bg-card dark:text-muted-foreground"
      >
        <option value="">+ Adicionar insumo</option>
        {grupos.map((g) => (
          <optgroup key={g.label} label={g.label}>
            {g.itens.map((m) => (
              <option key={m.id} value={m.id}>
                {rotuloDoMaterial(m)}
              </option>
            ))}
          </optgroup>
        ))}
        <option value={NOVO_MATERIAL}>+ Cadastrar insumo novo…</option>
      </select>
    </>
  );
}
